import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Chip,
  Menu,
  Table,
  Stack,
  Button,
  Tooltip,
  Divider,
  Checkbox,
  MenuItem,
  TextField,
  TableBody,
  Container,
  IconButton,
  ListItemText,
  LinearProgress,
  TableContainer,
  TablePagination,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';

// hooks
import useAuth from '../../../hooks/useAuth';
import useTabs from '../../../hooks/useTabs';
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator } from '../../../hooks/useTable';
import useColumnVisibility, { ColumnDef } from '../../../hooks/useColumnVisibility';
// @types
import { User } from '../../../@types/user';
// _mock_
// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  TableNoData,
  ColumnsButton,
  TableSkeleton,
  TableHeadCustom,
  TableSelectedActions,
} from '../../../components/table';
// sections
import { UserTableToolbar, UserTableRow } from "../../@dashboard/user/list";
import axiosInstance from 'src/utils/axios';
import ConfirmationDialog from './ConfirmationDialog';
import { APIS } from 'src/utils/serviceUrls';
import { UserModal } from './UserModal';
import AssignDiplomasDialog from './AssignDiplomasDialog';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

const Sort_Options = ['all', 'acsending', 'descending'];

// All toggleable Students columns, in display order. The checkbox + actions
// columns are fixed (not toggleable). The same ids gate the cells in
// UserTableRow, so header + body stay in sync.
const USER_COLUMNS: ColumnDef[] = [
  { id: 'Name', label: 'Name', align: 'left' },
  { id: 'Email', label: 'Email', align: 'left' },
  { id: 'Phone', label: 'Phone', align: 'left' },
  { id: 'Country', label: 'Country', align: 'left' },
  { id: 'Subscription', label: 'Subscription', align: 'left' },
  { id: 'Verified', label: 'Verified', align: 'center' },
  { id: 'Source', label: 'Source', align: 'left' },
  { id: 'SubStart', label: 'Sub. Start', align: 'left' },
  { id: 'SubEnd', label: 'Sub. End', align: 'left' },
  { id: 'Promo', label: 'Promo', align: 'left' },
  { id: 'Diploma', label: 'Diploma / Batch', align: 'left' },
  { id: 'Courses', label: 'Courses', align: 'left' },
  { id: 'Tracks', label: 'Tracks', align: 'left' },
  { id: 'status', label: 'Status', align: 'center' },
];
// Bumped to .v2 when the Tracks column was added, so admins with a saved
// column set still get the new column instead of it staying hidden.
const COLUMNS_STORAGE_KEY = 'kayfa.users.visibleColumns.v2';

// Phase: user segmentation — source values offered in the UI. `kaif_community`
// is intentionally omitted until the Community integration is built.
const SOURCE_OPTIONS = [
  { value: 'all', label: 'Any source' },
  { value: 'direct', label: 'Direct' },
  { value: 'admin', label: 'Admin-created' },
];

// Customizable filters: the admin chooses which of these show (the rest stay
// hidden, not applied). Default = a minimal set; the choice persists locally.
const FILTER_DEFS: { key: string; label: string }[] = [
  { key: 'source', label: 'Source' },
  { key: 'premium', label: 'Premium' },
  { key: 'free', label: 'Free access' },
  { key: 'diplomaId', label: 'Diploma' },
  { key: 'course', label: 'Course' },
  { key: 'track', label: 'Track' },
  { key: 'subStart', label: 'Sub start' },
  { key: 'subEnd', label: 'Sub end' },
];
const FILTERS_STORAGE_KEY = 'kayfa.users.visibleFilters';
const DEFAULT_VISIBLE_FILTERS = ['source', 'premium'];

// ----------------------------------------------------------------------

export default function UserList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultRowsPerPage: 10, defaultOrderBy: 'createdAt', defaultOrder: 'desc' });


  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  // Staff-roles on the Students surface. Every scoped role can READ this page;
  // the three capabilities below are what actually differ, and each mirrors a
  // BE @Auth list (the BE is the real boundary — these just hide dead buttons).
  //
  //   canWrite     create / bulk-delete / full edit → admin + super-admin only.
  //   canExport    bulk PII egress → admin + super-admin + sales-manager.
  //                customer-care and support are deliberately excluded.
  //   canAssignDiploma  customer-care's ONE write: assign or remove a diploma,
  //                via PATCH /users/:id/diplomas. Not the segment endpoint —
  //                that one carries freeAccess, i.e. free lifetime premium.
  const { user } = useAuth();
  const myRoles: string[] = ((user as any)?.roles as string[]) ?? [];
  const isFullAdmin = myRoles.includes('admin') || myRoles.includes('super-admin');
  const canWrite = isFullAdmin;
  const canExport = isFullAdmin || myRoles.includes('sales-manager');
  const canAssignDiploma = isFullAdmin || myRoles.includes('customer-care');

  // Phase: user segmentation — filters are URL-driven so segment counts on the
  // analytics page can deep-link here (e.g. ?source=eduverse&diplomaLive=true)
  // and a filtered view is shareable/bookmarkable.
  const [searchParams, setSearchParams] = useSearchParams();

  const [tableData, setTableData] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [filterName, setFilterName] = useState(searchParams.get('search') || '');

  const [filterRole, setFilterRole] = useState('all');
  // Phase: user segmentation — server-side segment filters (seeded from the URL).
  const [filterSource, setFilterSource] = useState(searchParams.get('source') || 'all');
  const [filterPremium, setFilterPremium] = useState(searchParams.get('isPremium') || 'all'); // all | true | false
  const [filterFree, setFilterFree] = useState(searchParams.get('freeAccess') || 'all'); // all | true | false
  const [filterDiploma, setFilterDiploma] = useState(
    searchParams.get('diplomaLive') === 'true' ? 'true' : 'all',
  ); // all | true
  const [filterTrack, setFilterTrack] = useState(searchParams.get('track') || '');
  // New diploma-entity filter (distinct from the legacy diplomaLive boolean above).
  const [filterDiplomaId, setFilterDiplomaId] = useState(searchParams.get('diploma') || '');
  // Course-enrolment filter: students enrolled in a specific course.
  const [filterCourse, setFilterCourse] = useState(searchParams.get('course') || '');
  const [includeStaff, setIncludeStaff] = useState(
    searchParams.get('includeStaff') === 'true',
  );
  const [subStart, setSubStart] = useState(searchParams.get('subStart') || '');
  const [subEnd, setSubEnd] = useState(searchParams.get('subEnd') || '');
  const [tracks, setTracks] = useState<any[]>([]);
  const [diplomas, setDiplomas] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  // id -> title, for the Diploma column.
  const diplomaMap: Record<string, string> = {};
  diplomas.forEach((d: any) => {
    if (d?._id) diplomaMap[d._id] = d.title;
  });
  // id -> title/name, so the Courses column can show course NAMES (the row
  // carries only course ids). Built from the courses already fetched for the
  // Course filter dropdown.
  const courseMap: Record<string, string> = {};
  courses.forEach((c: any) => {
    if (c?._id) courseMap[c._id] = c.title || c.name || '';
  });
  // id -> title, so the Tracks column can show track NAMES (rows carry ids).
  // Built from the tracks already fetched for the Track filter dropdown.
  const trackMap: Record<string, string> = {};
  tracks.forEach((t: any) => {
    if (t?._id) trackMap[t._id] = t.title || t.name || '';
  });
  const [modalOpen, setModalOpen] = useState(false);
  // customer-care diploma-only edit (see AssignDiplomasDialog).
  const [diplomaUser, setDiplomaUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<User | undefined>();

  const { currentTab: filterStatus } = useTabs('all');
  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });
  const [loading, setLoading] = useState<boolean>(false);
  // Bumped after a create/edit/delete to force a refetch even when the filter
  // state is unchanged (so a new record always shows up immediately).
  const [refreshTick, setRefreshTick] = useState(0);

  // Customizable filters: which filter controls are shown (persisted locally).
  const [visibleFilters, setVisibleFilters] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      /* ignore */
    }
    return DEFAULT_VISIBLE_FILTERS;
  });
  const [filtersMenuAnchor, setFiltersMenuAnchor] = useState<null | HTMLElement>(null);
  const showF = (key: string) => visibleFilters.includes(key);

  // Column show/hide via the shared hook (persisted locally).
  const { visibleColumns, toggleColumn, headLabel } = useColumnVisibility(
    USER_COLUMNS,
    COLUMNS_STORAGE_KEY
  );
  // Header = visible columns (in registry order) + the fixed actions column.
  const tableHead = [...headLabel, { id: '' }];



  // FE.4 (client-side-pagination-list-pages): server-side pagination + search.
  // Refetch whenever page / rowsPerPage / search changes. Search is debounced
  // (350ms) so typing doesn't fire a request per keystroke; page/size changes
  // fetch immediately. Page is 0-based in MUI, 1-based on the API.
  useEffect(() => {
    const t = setTimeout(
      () => {
        getUsersList().catch((err) => enqueueSnackbar(err?.message, { variant: 'error' }));
      },
      filterName ? 350 : 0
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    filterName,
    filterSource,
    filterPremium,
    filterFree,
    filterDiploma,
    filterDiplomaId,
    filterCourse,
    filterTrack,
    includeStaff,
    subStart,
    subEnd,
    refreshTick,
  ]);

  // Keep the URL in sync with the active filters (deep-link + shareable).
  useEffect(() => {
    const p: Record<string, string> = {};
    if (filterName) p.search = filterName;
    if (filterSource !== 'all') p.source = filterSource;
    if (filterPremium !== 'all') p.isPremium = filterPremium;
    if (filterFree !== 'all') p.freeAccess = filterFree;
    if (filterDiploma === 'true') p.diplomaLive = 'true';
    if (filterDiplomaId) p.diploma = filterDiplomaId;
    if (filterCourse) p.course = filterCourse;
    if (filterTrack) p.track = filterTrack;
    if (includeStaff) p.includeStaff = 'true';
    if (subStart) p.subStart = subStart;
    if (subEnd) p.subEnd = subEnd;
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterName,
    filterSource,
    filterPremium,
    filterFree,
    filterDiploma,
    filterDiplomaId,
    filterCourse,
    filterTrack,
    includeStaff,
    subStart,
    subEnd,
  ]);

  // Tracks for the "Track" filter dropdown.
  useEffect(() => {
    axiosInstance
      .get(APIS.TRACKS.LIST)
      .then((res) => {
        const d: any = res.data?.data ?? res.data;
        setTracks(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
      })
      .catch(() => setTracks([]));
  }, []);

  // Active diplomas for the Diploma filter dropdown + the Diploma column labels.
  useEffect(() => {
    axiosInstance
      .get(APIS.DIPLOMAS.ACTIVE)
      .then((res) => {
        const d: any = res.data?.data ?? res.data;
        setDiplomas(Array.isArray(d) ? d : []);
      })
      .catch(() => setDiplomas([]));
  }, []);

  // Courses for the "Course" filter dropdown (students enrolled in a course).
  useEffect(() => {
    axiosInstance
      .get(APIS.COURSES.LIST)
      .then((res) => {
        const d: any = res.data?.data ?? res.data;
        setCourses(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
      })
      .catch(() => setCourses([]));
  }, []);

  const getUsersList = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.USERS.PAGINATED, {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          search: filterName || undefined,
          source: filterSource !== 'all' ? filterSource : undefined,
          isPremium: filterPremium !== 'all' ? filterPremium : undefined,
          freeAccess: filterFree !== 'all' ? filterFree : undefined,
          diplomaLive: filterDiploma === 'true' ? 'true' : undefined,
          diploma: filterDiplomaId || undefined,
          course: filterCourse || undefined,
          track: filterTrack || undefined,
          includeStaff: includeStaff ? 'true' : undefined,
          subStart: subStart || undefined,
          subEnd: subEnd || undefined,
        },
      });
      const payload = res.data?.data ?? res.data;
      setTableData(payload?.items ?? []);
      setTotalCount(payload?.total ?? 0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
  };

  const handleFilterRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterRole(event.target.value);
  };

  const clearFilters = () => {
    setFilterSource('all');
    setFilterPremium('all');
    setFilterFree('all');
    setFilterDiploma('all');
    setFilterDiplomaId('');
    setFilterCourse('');
    setFilterTrack('');
    setIncludeStaff(false);
    setSubStart('');
    setSubEnd('');
    setPage(0);
  };

  // After a successful add/edit, clear every filter + search and jump to page 1
  // so the affected (newest) record is always visible at the top — then force a
  // refetch via refreshTick (covers the case where filters were already clear).
  const refreshAfterSave = () => {
    clearFilters();
    setFilterName('');
    setRefreshTick((t) => t + 1);
  };

  // Reset a single filter to its default (used when a filter is hidden, so a
  // hidden filter is never silently applied).
  const resetFilterValue = (key: string) => {
    if (key === 'source') setFilterSource('all');
    if (key === 'premium') setFilterPremium('all');
    if (key === 'free') setFilterFree('all');
    if (key === 'diploma') setFilterDiploma('all');
    if (key === 'diplomaId') setFilterDiplomaId('');
    if (key === 'course') setFilterCourse('');
    if (key === 'track') setFilterTrack('');
    if (key === 'subStart') setSubStart('');
    if (key === 'subEnd') setSubEnd('');
  };

  const toggleFilter = (key: string) => {
    const isHiding = visibleFilters.includes(key);
    setVisibleFilters((prev) => {
      const next = isHiding ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    if (isHiding) {
      resetFilterValue(key);
      setPage(0);
    }
  };

  const handleDeleteRows = (selected: string[]) => {
    const deleteRows = tableData?.filter((row) => !selected.includes(row._id));
    setSelected([]);
    setTableData(deleteRows);
  };

  const handleDeleteRow = async (id: string) => {
    try {
      setLoading(true);
      await axiosInstance.delete(APIS.USERS.BYId(id));
      setSelected([]);
      // FE.4: refetch from the server so the total + current page stay correct.
      await getUsersList();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditRow = (user: User) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  // Group B (7.18): download the users CSV. Uses axiosInstance so the auth
  // header is sent (a plain <a href> wouldn't carry the Bearer token).
  const handleExportCsv = async () => {
    try {
      const res = await axiosInstance.get(APIS.USERS.EXPORT_CSV, {
        responseType: 'blob',
        // Export the CURRENTLY-FILTERED segment, not the whole table.
        params: {
          search: filterName || undefined,
          source: filterSource !== 'all' ? filterSource : undefined,
          isPremium: filterPremium !== 'all' ? filterPremium : undefined,
          freeAccess: filterFree !== 'all' ? filterFree : undefined,
          diplomaLive: filterDiploma === 'true' ? 'true' : undefined,
          diploma: filterDiplomaId || undefined,
          course: filterCourse || undefined,
          track: filterTrack || undefined,
          includeStaff: includeStaff ? 'true' : undefined,
          subStart: subStart || undefined,
          subEnd: subEnd || undefined,
        },
      });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users-export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Export failed', { variant: 'error' });
    }
  };

  // FE.4: the server already filtered by search and returned one page, so we
  // pass filterName='' here (applySortFilter only re-orders the current page
  // via the column-sort comparator — it must NOT re-filter, or email/phone
  // matches whose name doesn't contain the term would be dropped).
  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName: '',
    filterRole,
    filterStatus,
  });

  const isNotFound = !dataFiltered.length && (!!filterName || totalCount === 0);

  return (
    <Page title="User: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="User List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'User List', href: PATH_DASHBOARD.user.root },
          ]}
          action={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {canExport && (
                <Button
                  variant="outlined"
                  onClick={handleExportCsv}
                  startIcon={<Iconify icon={'eva:download-outline'} />}
                >
                  Export CSV
                </Button>
              )}
              {canWrite && (
                <Button
                  variant="contained"
                  onClick={() => setModalOpen(true)}
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                >
                  New User
                </Button>
              )}
            </Box>
          }
        />

        <Card>
          <Divider />
          <UserTableToolbar
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
            optionsRole={Sort_Options}
          />

          {/* Customizable filters — the admin picks which filters show via the
              Filters menu; hidden filters are reset so they're never applied. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1.5 }}
          >
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={(e) => setFiltersMenuAnchor(e.currentTarget)}
              startIcon={<Iconify icon="eva:options-2-outline" />}
            >
              Filters
            </Button>
            <Menu
              anchorEl={filtersMenuAnchor}
              open={Boolean(filtersMenuAnchor)}
              onClose={() => setFiltersMenuAnchor(null)}
            >
              {FILTER_DEFS.map((f) => (
                <MenuItem key={f.key} onClick={() => toggleFilter(f.key)} dense>
                  <Checkbox checked={showF(f.key)} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={f.label} />
                </MenuItem>
              ))}
            </Menu>

            {/* Column show/hide — shared control. */}
            <ColumnsButton
              columns={USER_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />

            {showF('source') && (
              <TextField
                select
                size="small"
                label="Source"
                value={filterSource}
                onChange={(e) => {
                  setFilterSource(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 150 }}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('premium') && (
              <TextField
                select
                size="small"
                label="Premium"
                value={filterPremium}
                onChange={(e) => {
                  setFilterPremium(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="all">Any</MenuItem>
                <MenuItem value="true">Premium</MenuItem>
                <MenuItem value="false">Not premium</MenuItem>
              </TextField>
            )}
            {showF('free') && (
              <TextField
                select
                size="small"
                label="Free access"
                value={filterFree}
                onChange={(e) => {
                  setFilterFree(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">Any</MenuItem>
                <MenuItem value="true">Free / comped</MenuItem>
                <MenuItem value="false">Paying</MenuItem>
              </TextField>
            )}
            {showF('diplomaId') && (
              <TextField
                select
                size="small"
                label="Diploma"
                value={filterDiplomaId}
                onChange={(e) => {
                  setFilterDiplomaId(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">Any diploma</MenuItem>
                {diplomas.map((d: any) => (
                  <MenuItem key={d._id} value={d._id}>
                    {d.title}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('course') && (
              <TextField
                select
                size="small"
                label="Course"
                value={filterCourse}
                onChange={(e) => {
                  setFilterCourse(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Any course</MenuItem>
                {courses.map((c: any) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.title || c.name || c._id}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('track') && (
              <TextField
                select
                size="small"
                label="Track"
                value={filterTrack}
                onChange={(e) => {
                  setFilterTrack(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">Any track</MenuItem>
                {tracks.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    {t.title || t.name || t._id}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('subStart') && (
              <TextField
                type="date"
                size="small"
                label="Sub start"
                InputLabelProps={{ shrink: true }}
                value={subStart}
                onChange={(e) => {
                  setSubStart(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
              />
            )}
            {showF('subEnd') && (
              <TextField
                type="date"
                size="small"
                label="Sub end"
                InputLabelProps={{ shrink: true }}
                value={subEnd}
                onChange={(e) => {
                  setSubEnd(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
              />
            )}
            <Button
              size="small"
              color="inherit"
              onClick={clearFilters}
              startIcon={<Iconify icon="eva:close-fill" />}
            >
              Clear
            </Button>
            {/* Live count of students matching the active course/diploma filter. */}
            {(filterCourse || filterDiplomaId) && (
              <Chip
                color="info"
                icon={<Iconify icon="eva:people-fill" />}
                label={`${totalCount} student${totalCount === 1 ? '' : 's'} ${
                  filterCourse
                    ? `in ${courses.find((c: any) => c._id === filterCourse)?.title ||
                      courses.find((c: any) => c._id === filterCourse)?.name ||
                      'course'}`
                    : `in ${diplomas.find((d: any) => d._id === filterDiplomaId)?.title || 'diploma'}`
                }`}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>

            <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
              {loading && (
                <LinearProgress
                  sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
                />
              )}
              {selected.length > 0 && canWrite && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={tableData?.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id)
                    )
                  }
                  actions={
                    <Tooltip title="Delete">
                      <IconButton color="primary" onClick={() => handleDeleteRows(selected)}>
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}

              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 1100 }}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  rowCount={tableData?.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id)
                    )
                  }
                />

                <TableBody>
                  {loading ? (
                    // Skeleton placeholder rows while the page loads.
                    [...Array(Math.min(rowsPerPage || 5, 8))].map((_, i) => (
                      <TableSkeleton key={`skeleton-${i}`} />
                    ))
                  ) : (
                    <>
                      {/* FE.4: render the server-returned page directly (no client slice). */}
                      {dataFiltered.length > 0 &&
                        dataFiltered.map((row) => (
                          <UserTableRow
                            key={row._id}
                            row={row}
                            canWrite={canWrite}
                            canAssignDiploma={canAssignDiploma}
                            onAssignDiploma={() => setDiplomaUser(row)}
                            visibleColumns={visibleColumns}
                            diplomaMap={diplomaMap}
                            courseMap={courseMap}
                            trackMap={trackMap}
                            selected={selected.includes(row._id)}
                            onSelectRow={() => onSelectRow(row._id)}
                            onDeleteRow={() => setConfirmOpen({ open: true, currentId: row._id })}
                            onEditRow={() => handleEditRow(row)}
                          />
                        ))}

                      <TableNoData isNotFound={isNotFound} />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <UserModal
        isEdit={currentUser ? true : false}
        currentUser={currentUser}
        open={modalOpen}
        handleClose={(saved?: boolean) => {
          setModalOpen(false);
          setCurrentUser(undefined);
          // On a successful save, clear filters + go to page 1 so the new/edited
          // record is visible at the top; otherwise just refresh in place.
          if (saved) refreshAfterSave();
          else getUsersList();
        }}
      />
      <AssignDiplomasDialog
        open={!!diplomaUser}
        user={diplomaUser}
        onClose={(saved?: boolean) => {
          setDiplomaUser(null);
          if (saved) getUsersList();
        }}
      />
      <ConfirmationDialog
        isOpen={confirmOpen.open}
        onClose={() => setConfirmOpen({ open: false, currentId: '' })}
        confirmAction={() => handleDeleteRow(confirmOpen.currentId)}
        loading={loading}
      />
    </Page>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
  filterStatus,
  filterRole,
}: {
  tableData: User[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string;
  filterRole: string;
}) {
  const stabilizedThis = tableData?.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData?.filter(
      (item: Record<string, any>) =>
        item.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.status === filterStatus);
  }

  if (filterRole !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.role === filterRole);
  }

  return tableData;
}
